import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from './firebase';

const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--cv-bg-base, #F5F2E9)',
    fontFamily: 'var(--font-body, Inter, sans-serif)',
    padding: '24px 16px',
  },
  card: {
    background: 'var(--cv-bg-surface, #FEFCF6)',
    borderRadius: 24,
    padding: '40px 36px',
    maxWidth: 420,
    width: '100%',
    boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
    textAlign: 'center',
  },
  logo: {
    fontSize: 22,
    fontWeight: 800,
    color: 'var(--cv-teal, #004D40)',
    marginBottom: 32,
    letterSpacing: '-0.5px',
  },
  icon: { fontSize: 48, marginBottom: 16 },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--cv-teal, #004D40)',
    marginBottom: 8,
  },
  sub: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 1.6,
    marginBottom: 28,
  },
  btn: {
    display: 'inline-block',
    background: 'var(--cv-teal, #004D40)',
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    padding: '12px 32px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
    marginTop: 8,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    textAlign: 'left',
    marginBottom: 6,
    marginTop: 16,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1.5px solid #e5e7eb',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
    color: '#1a1a1a',
  },
  error: {
    fontSize: 13,
    color: '#dc2626',
    marginTop: 12,
    textAlign: 'left',
  },
};

function Card({ icon, title, sub, action }) {
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>CVFlow</div>
        <div style={S.icon}>{icon}</div>
        <div style={S.title}>{title}</div>
        <div style={S.sub}>{sub}</div>
        {action}
      </div>
    </div>
  );
}

export default function AuthAction() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode    = params.get('mode');
  const oobCode = params.get('oobCode');

  const [status, setStatus]       = useState('loading');
  const [errorMsg, setErrorMsg]   = useState('');
  const [pw1, setPw1]             = useState('');
  const [pw2, setPw2]             = useState('');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (!oobCode) { setStatus('error'); setErrorMsg('Ungültiger oder abgelaufener Link.'); return; }

    if (mode === 'verifyEmail' || mode === 'recoverEmail' || mode === 'verifyAndChangeEmail') {
      applyActionCode(auth, oobCode)
        .then(() => setStatus('verified'))
        .catch(() => { setStatus('error'); setErrorMsg('Der Link ist abgelaufen oder wurde bereits verwendet.'); });
    } else if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, oobCode)
        .then(() => setStatus('form'))
        .catch(() => { setStatus('error'); setErrorMsg('Der Link ist abgelaufen oder ungültig. Bitte fordere einen neuen an.'); });
    } else {
      setStatus('error');
      setErrorMsg('Unbekannte Aktion.');
    }
  }, [mode, oobCode]);

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) { setErrorMsg('Passwörter stimmen nicht überein.'); return; }
    if (pw1.length < 8) { setErrorMsg('Mindestens 8 Zeichen erforderlich.'); return; }
    setSaving(true);
    setErrorMsg('');
    try {
      await confirmPasswordReset(auth, oobCode, pw1);
      setStatus('reset-done');
    } catch {
      setErrorMsg('Fehler beim Speichern. Bitte fordere einen neuen Link an.');
      setSaving(false);
    }
  };

  if (status === 'loading') {
    return (
      <Card
        icon="⏳"
        title="Einen Moment…"
        sub="Wir überprüfen deinen Link."
      />
    );
  }

  if (status === 'verified') {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    return (
      <Card
        icon="✅"
        title="E-Mail bestätigt"
        sub={isMobile
          ? 'Deine Adresse ist verifiziert. Kehre zur CVFlow-App zurück und melde dich an.'
          : 'Deine E-Mail-Adresse wurde erfolgreich verifiziert. Du kannst dich jetzt anmelden.'}
        action={isMobile ? (
          <a href="cvflow://email-verified" style={S.btn}>App öffnen</a>
        ) : (
          <button style={S.btn} onClick={() => navigate('/login')}>Zur Anmeldung</button>
        )}
      />
    );
  }

  if (status === 'reset-done') {
    return (
      <Card
        icon="🔐"
        title="Passwort gesetzt"
        sub="Dein Passwort wurde erfolgreich geändert."
        action={<button style={S.btn} onClick={() => navigate('/login')}>Zur Anmeldung</button>}
      />
    );
  }

  if (status === 'error') {
    return (
      <Card
        icon="⚠️"
        title="Link ungültig"
        sub={errorMsg}
        action={<button style={S.btn} onClick={() => navigate('/login')}>Zur Anmeldung</button>}
      />
    );
  }

  // status === 'form' → Passwort-Reset-Formular
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>CVFlow</div>
        <div style={S.icon}>🔑</div>
        <div style={S.title}>Neues Passwort</div>
        <div style={S.sub}>Wähle ein neues Passwort für deinen Account.</div>
        <form onSubmit={submitNewPassword} style={{ textAlign: 'left' }}>
          <label style={S.label}>Neues Passwort</label>
          <input
            style={S.input}
            type="password"
            value={pw1}
            onChange={e => setPw1(e.target.value)}
            placeholder="Mindestens 8 Zeichen"
            required
            autoFocus
          />
          <label style={S.label}>Passwort wiederholen</label>
          <input
            style={S.input}
            type="password"
            value={pw2}
            onChange={e => setPw2(e.target.value)}
            placeholder="Passwort bestätigen"
            required
          />
          {errorMsg && <div style={S.error}>{errorMsg}</div>}
          <button
            type="submit"
            style={{ ...S.btn, width: '100%', marginTop: 24, opacity: saving ? 0.7 : 1 }}
            disabled={saving}
          >
            {saving ? 'Wird gespeichert…' : 'Passwort speichern'}
          </button>
        </form>
      </div>
    </div>
  );
}
